import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = 'https://card.flap.com.np/api';

const ACC_ICONS = { card:'💳', wallet:'👜', keyring:'🔑', ring:'💍', other:'📦' };

function hexToRgb(hex) {
  const c = (hex || '#f97316').replace('#', '');
  const f = c.length === 3 ? c.split('').map(x => x + x).join('') : c;
  const n = parseInt(f, 16);
  return `${(n >> 16) & 255},${(n >> 8) & 255},${n & 255}`;
}

// ── Exchange contact modal (finder shares info with owner) ─────────────────
function FinderModal({ colour, flapid, onClose }) {
  const [form, setForm] = useState({ name: '', phone: '', message: '' });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  const submit = async () => {
    if (!form.name || !form.phone) return alert('Please enter your name and phone number.');
    setSending(true);
    try {
      // POST to backend — log finder info so owner can retrieve
      await axios.post(`${API}/flapfindmy/${flapid}/finder`, form);
    } catch (_) { /* non-critical */ }
    setSent(true);
    setSending(false);
  };

  const rgb = hexToRgb(colour);

  return (
    <div style={{
      position:'fixed', inset:0, zIndex:200,
      background:'rgba(0,0,0,0.85)', backdropFilter:'blur(16px)',
      display:'flex', alignItems:'center', justifyContent:'center', padding:20,
    }}>
      <div style={{
        background:'#0f1623', border:`1px solid rgba(${rgb},0.3)`,
        borderRadius:20, padding:32, maxWidth:400, width:'100%',
        boxShadow:`0 24px 80px rgba(0,0,0,0.6)`,
        animation:'slideUp 0.3s ease',
      }}>
        {sent ? (
          <div style={{ textAlign:'center' }}>
            <div style={{ fontSize:52, marginBottom:12 }}>🎉</div>
            <div style={{ fontSize:20, fontWeight:800, color:'#e2e8f0', fontFamily:"'Syne',sans-serif", marginBottom:8 }}>Thank you!</div>
            <div style={{ fontSize:14, color:'#64748b', lineHeight:1.7, marginBottom:24 }}>
              Your contact has been logged. The owner will be notified and can reach you.
            </div>
            <button onClick={onClose} style={solidBtn(colour)}>Close</button>
          </div>
        ) : (
          <>
            <div style={{ fontSize:20, fontWeight:800, color:'#e2e8f0', fontFamily:"'Syne',sans-serif", marginBottom:4 }}>
              I Found This Item
            </div>
            <div style={{ fontSize:13, color:'#64748b', marginBottom:22, lineHeight:1.6 }}>
              Leave your contact so the owner can reach you. Your info is kept private.
            </div>

            {[
              ['Your Name *',    'name',    'text', 'e.g. Bikalpa'],
              ['Phone Number *', 'phone',   'tel',  '+977 98023654XX'],
              ['Message',        'message', 'text', 'Where did you find it?'],
            ].map(([lbl, key, type, ph]) => (
              <div key={key} style={{ marginBottom:14 }}>
                <label style={{ display:'block', fontSize:10, color:'#475569', marginBottom:5, textTransform:'uppercase', letterSpacing:1.2, fontWeight:700 }}>{lbl}</label>
                <input
                  type={type} value={form[key]}
                  onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))}
                  placeholder={ph}
                  style={{ width:'100%', background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:8, padding:'10px 14px', color:'#e2e8f0', fontSize:14, outline:'none', boxSizing:'border-box', fontFamily:"'DM Sans',sans-serif" }}
                />
              </div>
            ))}

            <div style={{ display:'flex', gap:10, marginTop:22 }}>
              <button onClick={onClose} style={{ flex:1, padding:11, borderRadius:8, border:'1px solid rgba(255,255,255,0.08)', background:'transparent', color:'#475569', cursor:'pointer', fontSize:14, fontFamily:"'DM Sans',sans-serif" }}>
                Cancel
              </button>
              <button onClick={submit} disabled={sending} style={{ ...solidBtn(colour), flex:2, opacity: sending ? 0.7 : 1 }}>
                {sending ? '⏳ Sending…' : '📨 Send My Contact'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function solidBtn(colour) {
  const rgb = hexToRgb(colour);
  return {
    padding: '11px 20px', borderRadius: 8, border: 'none',
    background: `linear-gradient(135deg, rgba(${rgb},0.9), ${colour})`,
    color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600,
    fontFamily: "'DM Sans',sans-serif", width: '100%', transition:'all 0.2s',
  };
}

// ── Main Component ─────────────────────────────────────────────────────────
export default function ContactOwner() {
  const { flapid: rawParam } = useParams();
  const navigate = useNavigate();
  const flapid   = (rawParam || '').replace(/^:+/, '').trim();

  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (!flapid) { setError('No FlapID'); setLoading(false); return; }
    axios.get(`${API}/flapfindmy/${encodeURIComponent(flapid)}`)
      .then(({ data: d }) => {
        if (!d.isLost) {
          // Item was found/recovered — redirect back to normal profile
          navigate(`/${flapid}`, { replace: true });
          return;
        }
        setData(d);
      })
      .catch(err => setError(err.response?.data?.error || 'Item not found'))
      .finally(() => setLoading(false));
  }, [flapid, navigate]);

  if (loading) return (
    <div style={centerStyle}>
      <div style={spinnerStyle} />
      <span style={{ color: '#64748b' }}>Loading…</span>
      <style>{kf}</style>
    </div>
  );

  if (error || !data) return (
    <div style={centerStyle}>
      <div style={{ fontSize: 48 }}>❓</div>
      <div style={{ fontSize: 18, color: '#e2e8f0' }}>Item not found</div>
      <div style={{ fontSize: 13, color: '#475569' }}>{error}</div>
      <button style={{ ...solidBtn('#38bdf8'), width:'auto', padding:'10px 24px', marginTop:12 }} onClick={() => navigate(-1)}>← Go Back</button>
      <style>{kf}</style>
    </div>
  );

  const colour = data.owner?.colour || '#f97316';
  const rgb    = hexToRgb(colour);
  const owner  = data.owner || {};

  return (
    <div style={{ minHeight:'100vh', background:'#080b14', fontFamily:"'DM Sans','Segoe UI',sans-serif", color:'#e2e8f0', position:'relative', overflow:'hidden' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=DM+Sans:wght@300;400;500;600&display=swap');
        ${kf}
        .finder-btn:hover { opacity: 0.88; transform: translateY(-2px); }
      `}</style>

      {showModal && <FinderModal colour={colour} flapid={flapid} onClose={() => setShowModal(false)} />}

      {/* ── Blurred background — owner's cover/photo tiled ── */}
      <div style={{ position:'fixed', inset:0, zIndex:0, overflow:'hidden' }}>
        {/* Blurred photo layer */}
        {owner.hasPhoto && (
          <img src={owner.photoUrl} alt=""
            style={{ position:'absolute', inset:0, width:'100%', height:'100%', objectFit:'cover', filter:'blur(40px) saturate(0.4)', transform:'scale(1.15)', opacity:0.25 }} />
        )}
        {/* Dark gradient over blur */}
        <div style={{ position:'absolute', inset:0, background:'linear-gradient(135deg,rgba(8,11,20,0.92),rgba(15,20,35,0.95))' }} />
        {/* Subtle colour glow from owner's accent */}
        <div style={{ position:'absolute', inset:0, background:`radial-gradient(ellipse 70% 60% at 50% 40%, rgba(${rgb},0.08) 0%, transparent 70%)` }} />
        {/* Grid texture */}
        <div style={{ position:'absolute', inset:0, backgroundImage:`repeating-linear-gradient(90deg,transparent,transparent 60px,rgba(${rgb},0.02) 60px,rgba(${rgb},0.02) 61px),repeating-linear-gradient(0deg,transparent,transparent 60px,rgba(${rgb},0.02) 60px,rgba(${rgb},0.02) 61px)` }} />
      </div>

      {/* ── Content ── */}
      <div style={{ position:'relative', zIndex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minHeight:'100vh', padding:'40px 20px', textAlign:'center' }}>

        {/* Pulsing lost badge */}
        <div style={{
          background:'rgba(252,129,129,0.12)', border:'1px solid rgba(252,129,129,0.3)',
          borderRadius:30, padding:'7px 22px', fontSize:11, fontWeight:800,
          color:'#fca5a5', letterSpacing:2, textTransform:'uppercase', marginBottom:28,
          animation:'pulse 2s ease-in-out infinite',
        }}>
          🔴 Lost Item
        </div>

        {/* Blurred owner avatar */}
        <div style={{
          position:'relative', width:110, height:110, borderRadius:'50%',
          border:`3px solid rgba(${rgb},0.4)`,
          boxShadow:`0 0 0 6px rgba(${rgb},0.08), 0 16px 48px rgba(0,0,0,0.5)`,
          overflow:'hidden', marginBottom:24, background:'#111827',
        }}>
          {owner.hasPhoto ? (
            <>
              <img src={owner.photoUrl} alt="owner"
                style={{ width:'100%', height:'100%', objectFit:'cover', filter:'blur(10px)', transform:'scale(1.2)' }} />
              <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', background:'rgba(8,11,20,0.4)' }}>
                <span style={{ fontSize:36 }}>🔒</span>
              </div>
            </>
          ) : (
            <div style={{ width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:40, color:`rgba(${rgb},0.6)` }}>
              🔒
            </div>
          )}
        </div>

        {/* Accessory type + id */}
        <div style={{ fontSize:13, color:'#475569', marginBottom:8, display:'flex', alignItems:'center', gap:8, justifyContent:'center' }}>
          <span style={{ fontSize:18 }}>{ACC_ICONS[data.accType] || '📦'}</span>
          <span style={{ fontFamily:"'Courier New',monospace", color:'#334155' }}>{flapid}</span>
        </div>

        {/* Main heading */}
        <h1 style={{ fontFamily:"'Syne',sans-serif", fontSize:28, fontWeight:800, marginBottom:8, letterSpacing:-0.5, lineHeight:1.2 }}>
          This item has been<br />
          <span style={{ color: colour }}>reported lost</span>
        </h1>

        <p style={{ fontSize:15, color:'#64748b', marginBottom:data.lostMessage ? 16 : 28, maxWidth:360, lineHeight:1.7 }}>
          It belongs to <strong style={{ color:'#e2e8f0' }}>{owner.firstName || 'someone'}</strong>.
          If you found it, please help return it.
        </p>

        {/* Owner's custom message */}
        {data.lostMessage && (
          <div style={{
            background:'rgba(255,255,255,0.04)', border:`1px solid rgba(${rgb},0.2)`,
            borderRadius:16, padding:'16px 22px', maxWidth:380, marginBottom:28,
            fontSize:14, color:'#94a3b8', fontStyle:'italic', lineHeight:1.8,
            position:'relative',
          }}>
            <div style={{ position:'absolute', top:-8, left:20, background:'#080b14', padding:'0 8px', fontSize:11, color: colour, fontWeight:700, letterSpacing:1, textTransform:'uppercase', fontStyle:'normal' }}>
              Owner's Message
            </div>
            "{data.lostMessage}"
          </div>
        )}

        {/* Masked phone hint */}
        {owner.phoneMasked && (
          <div style={{ fontSize:13, color:'#334155', marginBottom:28 }}>
            📞 Owner's number ends in <strong style={{ color:'#64748b', fontFamily:"'Courier New',monospace" }}>{owner.phoneMasked.slice(-4)}</strong>
          </div>
        )}

        {/* Lost date */}
        {data.lostAt && (
          <div style={{ fontSize:12, color:'#1e293b', marginBottom:28 }}>
            Reported lost on {new Date(data.lostAt).toLocaleDateString(undefined, { year:'numeric', month:'long', day:'numeric' })}
          </div>
        )}

        {/* ── Action buttons ── */}
        <div style={{ display:'flex', flexDirection:'column', gap:12, width:'100%', maxWidth:340 }}>

          {/* Primary — I found this item */}
          <button className="finder-btn" onClick={() => setShowModal(true)} style={{
            padding:'14px', borderRadius:12, border:'none', cursor:'pointer',
            background:`linear-gradient(135deg,rgba(${rgb},0.85),${colour})`,
            color:'#fff', fontWeight:700, fontSize:15,
            fontFamily:"'DM Sans',sans-serif",
            boxShadow:`0 8px 28px rgba(${rgb},0.35)`,
            transition:'all 0.2s',
          }}>
            📍 I Found This Item
          </button>

          {/* Flap support email */}
          <a href={`mailto:${data.flapSupport?.email}?subject=Lost item found: ${flapid}&body=I found a Flap accessory with ID: ${flapid}`}
            className="finder-btn"
            style={{
              display:'block', padding:'13px', borderRadius:12, textAlign:'center', textDecoration:'none',
              background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.08)',
              color:'#94a3b8', fontWeight:600, fontSize:14,
              fontFamily:"'DM Sans',sans-serif", transition:'all 0.2s',
            }}>
            📧 Contact Flap Support
          </a>

          {/* WhatsApp support */}
          {data.flapSupport?.whatsapp && (
            <a href={data.flapSupport.whatsapp} target="_blank" rel="noreferrer"
              className="finder-btn"
              style={{
                display:'block', padding:'13px', borderRadius:12, textAlign:'center', textDecoration:'none',
                background:'rgba(37,211,102,0.08)', border:'1px solid rgba(37,211,102,0.2)',
                color:'#4ade80', fontWeight:600, fontSize:14,
                fontFamily:"'DM Sans',sans-serif", transition:'all 0.2s',
              }}>
              💬 WhatsApp Support
            </a>
          )}
        </div>

        {/* Footer */}
        <div style={{ marginTop:40, fontSize:12, color:'#1e293b' }}>
          Powered by <span style={{ color: colour }}>Flap</span> · Lost Item Recovery
        </div>
      </div>
    </div>
  );
}

// ── Shared ─────────────────────────────────────────────────────────────────
const kf = `
  @keyframes spin    { to { transform: rotate(360deg); } }
  @keyframes fadeUp  { from { opacity:0; transform:translateY(14px); } to { opacity:1; transform:translateY(0); } }
  @keyframes pulse   { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.7;transform:scale(0.98)} }
  @keyframes slideUp { from { opacity:0; transform:translateY(30px); } to { opacity:1; transform:translateY(0); } }
`;

const centerStyle = {
  minHeight:'100vh', display:'flex', flexDirection:'column', alignItems:'center',
  justifyContent:'center', background:'#080b14', gap:14,
  fontFamily:"'DM Sans',sans-serif", color:'#e2e8f0',
};
const spinnerStyle = {
  width:36, height:36, border:'3px solid rgba(249,115,22,0.15)',
  borderTopColor:'#f97316', borderRadius:'50%', animation:'spin 0.75s linear infinite',
};